'use client';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
export default function MediaQuestionEdit({
  question,
  setQuestion,
  errorMessage,
  helperText,
  handleInformation,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const getActiveMediaType = () => {
    const type = question?.multimedia?.type;
    return ['youtube', 'image', 'video'].includes(type) ? type : null;
  };

  const handleUpload = (event, type) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Configuración según tipo
    const config = {
      image: {
        maxSizeMB: 5,
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
        errorMsg: 'Solo se permiten imágenes en formato JPG, PNG, GIF o WEBP.',
      },
      video: {
        maxSizeMB: 50,
        allowedTypes: ['video/mp4', 'video/webm', 'video/ogg'],
        errorMsg: 'Solo se permiten videos en formato MP4, WebM u OGG.',
      },
    };

    const { maxSizeMB, allowedTypes, errorMsg } = config[type];
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    // Validar extensión MIME
    const invalidFile = files.find((f) => !allowedTypes.includes(f.type));
    if (invalidFile) {
      enqueueSnackbar(errorMsg, {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      return;
    }

    // Validar tamaño
    const oversized = files.find((f) => f.size > maxSizeBytes);
    if (oversized) {
      enqueueSnackbar(
        `El archivo "${oversized.name}" supera el límite de ${maxSizeMB}MB.`,
        {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        },
      );
      return;
    }

    // Actualizar estado
    const firstFile = files[0];
    const fileUrl = URL.createObjectURL(firstFile);

    setQuestion((prev) => ({
      ...prev,
      youtubeUrl: '',
      images: type === 'image' ? files : [],
      videos: type === 'video' ? files : [],
      multimedia: {
        type,
        urlMedia: fileUrl,
        fileName: firstFile.name,
      },
    }));
  };

  const removeMedia = (type, index) => {
    setQuestion((prev) => {
      const updated = [...(prev[type] || [])].filter((_, i) => i !== index);
      return {
        ...prev,
        [type]: updated,
        multimedia:
          updated.length === 0 ? { type: '', urlMedia: '' } : prev.multimedia,
      };
    });
  };

  const clearAllMultimedia = () => {
    setQuestion((prev) => ({
      ...prev,
      multimedia: { type: '', urlMedia: '', fileName: '' },
      images: [],
      videos: [],
      youtubeUrl: '',
    }));
  };

  const getMultimediaValueByType = (type) => {
    return question?.multimedia?.type === type
      ? question.multimedia.urlMedia
      : '';
  };

  const getMediaCount = () => {
    const imageCount = question?.images?.length || 0;
    const videoCount = question?.videos?.length || 0;
    const youtubeCount = question?.youtubeUrl ? 1 : 0;
    return imageCount + videoCount + youtubeCount;
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {errorMessage.multimedia?.general &&
        question.multimedia.type !== 'youtube' && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {helperText.multimedia?.general || 'Error en multimedia'}
          </Alert>
        )}
      <Card elevation={3} sx={{ borderRadius: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={4}>
            {/* YouTube Section */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}
              >
                <Box
                  sx={{
                    bgcolor: '#FF0000',
                    color: 'white',
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayArrowIcon />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Video de YouTube
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inserta un enlace de YouTube para tu pregunta
                  </Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                placeholder="https://www.youtube.com/watch?v=..."
                name="youtubeUrl"
                value={getMultimediaValueByType('youtube')}
                onChange={handleInformation}
                disabled={
                  getActiveMediaType() === 'image' ||
                  getActiveMediaType() === 'video'
                }
                error={
                  errorMessage?.youtubeUrl ||
                  (errorMessage?.multimedia?.youtube &&
                    question?.multimedia?.type === 'youtube')
                }
                helperText={
                  helperText?.youtubeUrl ||
                  (errorMessage?.multimedia?.youtube &&
                  question?.multimedia?.type === 'youtube'
                    ? helperText?.multimedia?.youtubeMessage
                    : '')
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlayArrowIcon sx={{ color: '#FF0000' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor:
                      getActiveMediaType() === 'image' ||
                      getActiveMediaType() === 'video'
                        ? 'grey.100'
                        : 'white',
                  },
                }}
              />
            </Paper>

            {/* Images Section */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ImageIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Imágenes
                  </Typography>
                </Box>
                {/*
                {question?.images?.length > 0 && (
                  <Badge
                    badgeContent={question.videos.length}
                    color="secondary"
                  >
                    <VideocamIcon color="action" />
                  </Badge>
                )}*/}
              </Box>

              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                id="upload-images"
                onChange={(e) => handleUpload(e, 'image')}
                disabled={
                  getActiveMediaType() === 'video' ||
                  getActiveMediaType() === 'youtube'
                }
              />

              <label htmlFor="upload-images">
                <Button
                  component="span"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  disabled={
                    getActiveMediaType() === 'video' ||
                    getActiveMediaType() === 'youtube'
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    mb: 2,
                  }}
                >
                  Subir Imagen (máx. 5MB)
                </Button>
              </label>

              {question?.multimedia?.type === 'image' &&
                question?.multimedia?.urlMedia && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={
                        question.multimedia.fileName ||
                        question.multimedia.urlMedia.name ||
                        'imagen subida'
                      }
                      onDelete={() =>
                        setQuestion((prev) => ({
                          ...prev,
                          multimedia: { type: '', urlMedia: '', fileName: '' },
                          images: [],
                        }))
                      }
                      deleteIcon={<CloseIcon />}
                      variant="filled"
                      color="primary"
                      sx={{
                        maxWidth: 300,
                        '& .MuiChip-label': {
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Box>
                )}
            </Paper>

            {/* Videos Section */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}
              >
                <Box
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <VideocamIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Videos
                  </Typography>
                </Box>
                {/*
                {question?.videos?.length > 0 && (
                  <Badge
                    badgeContent={question.videos.length}
                    color="secondary"
                  >
                    <VideocamIcon color="action" />
                  </Badge>
                )}*/}
              </Box>

              <input
                type="file"
                accept="video/*"
                multiple
                hidden
                id="upload-videos"
                onChange={(e) => handleUpload(e, 'video')}
                disabled={
                  getActiveMediaType() === 'image' ||
                  getActiveMediaType() === 'youtube'
                }
              />

              <label htmlFor="upload-videos">
                <Button
                  component="span"
                  variant="contained"
                  color="secondary"
                  startIcon={<CloudUploadIcon />}
                  disabled={
                    getActiveMediaType() === 'image' ||
                    getActiveMediaType() === 'youtube'
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    mb: 2,
                  }}
                >
                  Subir Video (máx. 50MB)
                </Button>
              </label>

              {question?.multimedia?.type === 'video' &&
                question?.multimedia?.urlMedia && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={question.multimedia.fileName || 'video subido'}
                      onDelete={() =>
                        setQuestion((prev) => ({
                          ...prev,
                          multimedia: { type: '', urlMedia: '', fileName: '' },
                          videos: [],
                        }))
                      }
                      deleteIcon={<CloseIcon />}
                      variant="filled"
                      color="secondary"
                      sx={{
                        maxWidth: 300,
                        '& .MuiChip-label': { fontWeight: 500 },
                      }}
                    />
                  </Box>
                )}
            </Paper>

            {/* Action Buttons 
            {getMediaCount() > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={clearAllMultimedia}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                  }}
                >
                  Limpiar Todo el Multimedia
                </Button>
              </Box>
            )}*/}

            {/* Info Alert */}
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Nota:</strong> Solo puedes seleccionar un tipo de
                multimedia por pregunta. Los campos se deshabilitarán
                automáticamente cuando selecciones una opción.
              </Typography>
            </Alert>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
